'use client';

/**
 * ProductSearchResultsHybridCard — mode11-only EXPERIMENTAL fork of
 * ProductSearchResultsCard, with the Hybrid Filters design's header
 * ACTUALLY in the canvas (not the rail — the first build of this
 * experiment wrongly put the refine box in the explorer's Filter lens;
 * this corrects that). mode4's mode4_search preset keeps mounting the
 * original, unmodified ProductSearchResultsCard.
 *
 * What's new vs. the original:
 *   - A sticky canvas header: query title + count (as Discovery's
 *     intent_grid canvas already does), a compact "Refine" text input
 *     (deterministic keyword-match against the response's OWN facet
 *     vocabulary, no LLM call — same mechanism as the real-data Hybrid
 *     Filters artifact), applied-filter chips, and a horizontal
 *     pill-chip row PER FACET with counts.
 *   - The pill-chip row is a direct visual borrow of Discovery's own
 *     `ChipRow` (components/canvas/cards/intent/swimlane-card.tsx) —
 *     same rounded-pill-with-count styling, not a new visual language
 *     invented for this experiment. Adapted here for multi-select
 *     (product_search filters are OR-within-facet, AND-across-facet;
 *     intent_grid's pool facets are single-select per axis).
 *   - Dead-chip dimming: a facet value with sibling-drilldown count 0
 *     stays visible, dimmed, rather than reading identically to a
 *     live one.
 *   - A writable price range (single max-price slider) — the rail's
 *     original RangeBody is explicitly read-only; the backend already
 *     accepts `filters.price = {min, max}`.
 *
 * Grid/tile/shelf rendering below the header is copied verbatim from
 * search-results-card.tsx (untouched there) — this fork only adds the
 * header; it doesn't change how results paint.
 *
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="product_search_results_hybrid"
 * @see components/canvas/cards/intent/swimlane-card.tsx — ChipRow, the borrowed visual pattern
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronUp, Loader2, Search, Sparkles, SlidersHorizontal, X } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import {
  useProductSearchStore,
  type ProductSearchFilters,
} from '@/lib/stores/product-search-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useTabStore } from '@/lib/stores/tab-store';
import { useProductLabel } from '@/lib/stores/total-matrix-store';
import { CardPlacementProvider, useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import type { ProductSummary } from '@/lib/api/types';
import type { Facet } from './facet-card';
import { ProductTile } from '../vendors/product-tile';
import { CardGrid } from '../card-grid';
import { SwimlaneCard } from '../swimlane-card';
import { type ResultSummary } from './result-summary-card';

interface ProductHit {
  semantic_id: string;
  name: string | null;
  price: number | null;
  category: string | null;
  image_url: string | null;
  vendor_name: string | null;
  vendor_sid: string | null;
  color: string | null;
  material: string | null;
  score?: number;
  /** No list price: the vendor quotes it ($0 / $1 placeholder in the catalogue). */
  price_on_request?: boolean;
  saved?: boolean;
}

interface VendorShelf {
  vendor_sid: string;
  vendor_name: string;
  count: number;
  monogram: { initials: string; bg?: string; fg?: string };
  items: ProductHit[];
}

interface Suggestion {
  type: string;
  mode: string;
  name: string;
  semantic_id: string;
  match: string;
}

interface ProductSearchResponse {
  products: ProductHit[];
  total: number;
  returned: number;
  capped: boolean;
  facets: Facet[];
  no_match?: boolean;
  suggestion?: Suggestion | null;
  grouped_by?: string;
  summary?: ResultSummary;
  shelves?: VendorShelf[];
  other_vendors?: { vendor_count: number; count: number } | null;
}

function toSummary(hit: ProductHit): ProductSummary {
  return {
    semantic_id: hit.semantic_id,
    name: hit.name ?? 'Unnamed',
    list_price: hit.price,
    qty_available: null,
    image_url: hit.image_url,
    saved: hit.saved ?? false,
  };
}

/** Same isolation rule as the original card (mode4's search isolation
 *  guard) — copied, not imported, so this fork touches zero lines of
 *  the shared file. See search-results-card.tsx's useSearchState for
 *  the full rationale (frozen-prop bug this pattern fixes). */
function useSearchState(initialQuery?: string) {
  const isolated = initialQuery != null;

  const gQuery = useProductSearchStore((s) => s.query);
  const gFilters = useProductSearchStore((s) => s.filters);
  const gSetFilter = useProductSearchStore((s) => s.setFilter);

  const [lFilters, setLFilters] = useState<ProductSearchFilters>({});
  const lSetFilter = useCallback((key: string, value: unknown | null) => {
    setLFilters((f) => {
      const next = { ...f };
      if (value == null) delete next[key];
      else next[key] = value;
      return next;
    });
  }, []);

  return isolated
    ? {
        query: initialQuery ?? '',
        filters: lFilters,
        setFilter: lSetFilter,
      }
    : {
        query: gQuery,
        filters: gFilters,
        setFilter: gSetFilter,
      };
}

/** Deterministic text → facet-term matcher, built fresh from whatever
 *  the CURRENT response's own facets actually are — never a hardcoded
 *  vocabulary. Same mechanism proved out in the real-data Hybrid
 *  Filters artifact. Negation ("drop leather") is deliberately absent:
 *  the backend's `_filter` has no exclusion operator at all. */
function buildLookup(facets: Facet[]): Map<string, { key: string; value: string }> {
  const lookup = new Map<string, { key: string; value: string }>();
  for (const f of facets) {
    if (f.type === 'range') continue;
    for (const v of f.values ?? []) {
      lookup.set(v.value.toLowerCase(), { key: f.key, value: v.value });
      if (v.label) lookup.set(v.label.toLowerCase(), { key: f.key, value: v.value });
    }
  }
  return lookup;
}

const PRICE_RE = /(?:under|below|less than|<|max)\s*\$?\s*(\d+)(k?)/i;

function parseRefineText(
  input: string,
  lookup: Map<string, { key: string; value: string }>,
): { matched: { key: string; value: string; term: string }[]; maxPrice: number | null; unmapped: string[] } {
  const parts = input
    .toLowerCase()
    .split(/,|\band\b/)
    .map((p) => p.trim())
    .filter(Boolean);
  const matched: { key: string; value: string; term: string }[] = [];
  const unmapped: string[] = [];
  let maxPrice: number | null = null;

  for (const part of parts) {
    const priceMatch = part.match(PRICE_RE);
    if (priceMatch) {
      maxPrice = Number(priceMatch[1]) * (priceMatch[2] ? 1000 : 1);
      continue;
    }
    const hit = lookup.get(part) ?? lookup.get(part.replace(/s$/, ''));
    if (hit) matched.push({ ...hit, term: part });
    else unmapped.push(part);
  }
  return { matched, maxPrice, unmapped };
}

export function ProductSearchResultsHybridCard({
  group_by = 'none',
  initialQuery,
}: {
  group_by?: string;
  initialQuery?: string;
}) {
  const { query, filters, setFilter } = useSearchState(initialQuery);
  const strategyName = useCapabilityStore((s) => s.strategyName('product_intelligence', 'search'));
  const [expandedLanes, setExpandedLanes] = useState<Set<string>>(new Set());
  const toggleLane = (sid: string) =>
    setExpandedLanes((s) => {
      const next = new Set(s);
      next.has(sid) ? next.delete(sid) : next.add(sid);
      return next;
    });
  const inspectEntity = useGraphSelectionStore((s) => s.inspectEntity);
  const inspectedEntity = useGraphSelectionStore((s) => s.inspectedEntity);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const setActiveExplorerView = useLayoutStore((s) => s.setActiveExplorerView);
  const openTab = useTabStore((s) => s.openTab);

  const labelEmpty = useProductLabel('search_results_empty');
  const labelSwitch = useProductLabel('search_switch');
  const noProductsTpl = useProductLabel('search_no_products');
  const foundInTpl = useProductLabel('search_found_in');
  const showAllLabel = useProductLabel('search_show_all');
  const priceOnRequestLabel = useProductLabel('search_price_on_request');
  const collapseLabel = useProductLabel('search_collapse');
  const loadMoreLabel = useProductLabel('wall_load_more');
  const countTpl = useProductLabel('search_count');
  const refinePlaceholder = useProductLabel('filter_refine_placeholder');
  const refineButtonLabel = useProductLabel('filter_refine_button');
  const clearAllLabel = useProductLabel('filter_clear_all');
  const priceAnyLabel = useProductLabel('filter_price_any');
  const hideFacetsLabel = useProductLabel('filter_hide_facets');
  const showFacetsLabel = useProductLabel('filter_show_facets');
  const appliedLabel = useProductLabel('filter_applied_label');

  const [refineText, setRefineText] = useState('');
  const [refineFeedback, setRefineFeedback] = useState<{ unmapped: string[] } | null>(null);
  const unmappedFeedbackLabel = useProductLabel('filter_refine_unmapped', {
    terms: (refineFeedback?.unmapped ?? []).join(', '),
  });

  const [page, setPage] = useState(0);
  const [priorPages, setPriorPages] = useState<ProductHit[]>([]);
  const searchKey = useMemo(
    () => JSON.stringify({ query, filters, group_by }),
    [query, filters, group_by],
  );
  useEffect(() => {
    setPage(0);
    setPriorPages([]);
  }, [searchKey]);

  const params = useMemo(
    () => ({ query, filters, limit: 150, page, group_by }),
    [query, filters, page, group_by],
  );
  // No query + no filters is a real state now (mode11's default
  // landing canvas, 2026-10-04 — see ui_config/composition/modes/
  // mode11.py's mode11_catalog preset), not an "idle, nothing to show"
  // state: browse mode with an empty filter set already returns the
  // full catalogue correctly (verified against the live graph this
  // session), so fetching is unconditional once the strategy resolves.
  const enabled = !!strategyName;
  const { data, isLoading, error } = useStrategyData<ProductSearchResponse>(
    strategyName,
    params,
    enabled,
  );
  const res = data?.data;
  const grouping = res?.grouped_by ?? 'none';
  const facets = res?.facets ?? [];
  const lookup = useMemo(() => buildLookup(facets), [facets]);

  const queryKey = useMemo(
    () => ['strategy-data', strategyName, params] as const,
    [strategyName, params],
  );

  useCardExport(
    useMemo(() => {
      const status: 'loading' | 'loaded' | 'error' | 'idle' = !enabled
        ? 'idle'
        : isLoading
          ? 'loading'
          : error
            ? 'error'
            : res
              ? 'loaded'
              : 'idle';
      return {
        label: 'Results (hybrid)',
        content_key: 'product_search_results_hybrid',
        surface: 'canvas' as const,
        status,
        query_key: enabled ? queryKey : null,
        text: query ? `${query} — ${res?.returned ?? 0} of ${res?.total ?? 0}` : 'Results',
        json: res ? { query, total: res.total, returned: res.returned, capped: res.capped } : null,
      };
    }, [enabled, isLoading, error, res, query, queryKey]),
  );

  // ── Facet interaction — same semantics as the rail's facet card ────
  const sel = (f: Facet) => (filters[f.key] as string[] | undefined) ?? [];
  const toggleValue = (key: string, value: string) => {
    const current = (filters[key] as string[] | undefined) ?? [];
    const next = current.includes(value) ? current.filter((x) => x !== value) : [...current, value];
    setFilter(key, next.length ? next : null);
  };
  const clearAll = () => Object.keys(filters).forEach((k) => setFilter(k, null));

  const applyRefine = () => {
    const txt = refineText.trim();
    if (!txt) return;
    const { matched, maxPrice, unmapped } = parseRefineText(txt, lookup);
    const byKey = new Map<string, string[]>();
    for (const m of matched) {
      const arr = byKey.get(m.key) ?? [];
      arr.push(m.value);
      byKey.set(m.key, arr);
    }
    byKey.forEach((values, key) => setFilter(key, values));
    if (maxPrice != null) setFilter('price', { max: maxPrice });
    setRefineFeedback({ unmapped });
    setRefineText('');
  };

  const chipsActive = Object.keys(filters).length > 0;
  const priceFacet = facets.find((f) => f.key === 'price');
  const priceFilter = filters.price as { min?: number; max?: number } | undefined;

  if (!enabled) {
    return (
      <Centered>
        <Search className="w-10 h-10 text-muted-foreground opacity-40" />
        <p className={text.muted}>{labelEmpty}</p>
      </Centered>
    );
  }

  if (isLoading && !res && priorPages.length === 0) {
    return (
      <Centered>
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </Centered>
    );
  }

  if (error) {
    return (
      <Centered>
        <p className="text-destructive">{(error as Error).message}</p>
      </Centered>
    );
  }

  const switchTo = (s: Suggestion) => {
    setActiveExplorerView(s.mode);
    openTab({ id: `${s.type}-${s.semantic_id}`, type: s.type, title: s.name, entityId: s.semantic_id });
    selectNode({ id: s.semantic_id, label: s.name, type: s.type });
  };

  if (res?.no_match && res.suggestion) {
    const s = res.suggestion;
    return (
      <Centered>
        <Search className="w-8 h-8 text-muted-foreground opacity-40" />
        <p className={text.muted}>{noProductsTpl.replace('{query}', query)}</p>
        <button
          onClick={() => switchTo(s)}
          className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-sm hover:border-primary hover:bg-primary/10 transition-colors"
        >
          {foundInTpl.replace('{type}', s.type.toUpperCase())}{' '}
          <span className="font-medium">{s.name}</span> · {labelSwitch}
        </button>
      </Centered>
    );
  }

  const onPick = (hit: ProductHit) =>
    inspectEntity({ id: hit.semantic_id, label: hit.name ?? '', type: 'product' });

  const tile = (hit: ProductHit, slotVendor: string, showVendor = false) => (
    <CardPlacementProvider
      key={hit.semantic_id}
      value={{ surface: 'canvas', slot_id: `product:${slotVendor}:${hit.semantic_id}` }}
    >
      <ProductTile
        product={toSummary(hit)}
        vendorId={hit.vendor_sid ?? ''}
        vendorName={hit.vendor_name ?? ''}
        showVendor={showVendor}
        queryKey={queryKey}
        collectionName=""
        onClick={() => onPick(hit)}
        isSelected={inspectedEntity?.id === hit.semantic_id}
        priceText={hit.price_on_request ? priceOnRequestLabel : undefined}
      />
    </CardPlacementProvider>
  );

  return (
    <div className="h-full overflow-y-auto">
      <HybridHeader
        query={query}
        total={res?.total ?? 0}
        refineText={refineText}
        onRefineChange={setRefineText}
        onRefineSubmit={applyRefine}
        refinePlaceholder={refinePlaceholder}
        refineButtonLabel={refineButtonLabel}
        feedback={
          // The "applied" half is dropped — it duplicated the persistent
          // Applied chip row below (same terms, said twice, one of them
          // disappearing). "Unmapped" terms are kept: they're the one
          // thing nothing else on this header surfaces.
          refineFeedback && refineFeedback.unmapped.length > 0 ? unmappedFeedbackLabel : null
        }
        facets={facets}
        selectedOf={sel}
        onToggle={toggleValue}
        chipsActive={chipsActive}
        onClearAll={clearAll}
        clearAllLabel={clearAllLabel}
        priceFacet={priceFacet}
        priceFilter={priceFilter}
        onSetPriceRange={(r) => setFilter('price', r)}
        hideFacetsLabel={hideFacetsLabel}
        showFacetsLabel={showFacetsLabel}
        priceAnyLabel={priceAnyLabel}
        appliedLabel={appliedLabel}
      />

      <div className="p-4 space-y-3">
        {grouping === 'none' ? (
          <FlatWall
            res={res}
            priorPages={priorPages}
            setPriorPages={setPriorPages}
            setPage={setPage}
            isLoading={isLoading}
            countTpl={countTpl}
            loadMoreLabel={loadMoreLabel}
            tile={tile}
          />
        ) : (
          (res?.shelves ?? []).map((shelf) => (
            <CardPlacementProvider
              key={shelf.vendor_sid}
              value={{ surface: 'canvas', slot_id: `vendor_shelf:${shelf.vendor_sid}` }}
            >
              <SwimlaneCard
                title={shelf.vendor_name}
                count={shelf.count}
                laneId={shelf.vendor_sid}
                contentKey="vendor_shelf"
                expanded={expandedLanes.has(shelf.vendor_sid)}
                onExpandToggle={() => toggleLane(shelf.vendor_sid)}
                expandLabel={showAllLabel}
                collapseLabel={collapseLabel}
              >
                {shelf.items.map((hit) => tile(hit, shelf.vendor_sid))}
              </SwimlaneCard>
            </CardPlacementProvider>
          ))
        )}
      </div>
    </div>
  );
}

function FlatWall({
  res,
  priorPages,
  setPriorPages,
  setPage,
  isLoading,
  countTpl,
  loadMoreLabel,
  tile,
}: {
  res: ProductSearchResponse | undefined;
  priorPages: ProductHit[];
  setPriorPages: (fn: (prev: ProductHit[]) => ProductHit[]) => void;
  setPage: (fn: (p: number) => number) => void;
  isLoading: boolean;
  countTpl: string;
  loadMoreLabel: string;
  tile: (hit: ProductHit, slotVendor: string, showVendor?: boolean) => React.ReactNode;
}) {
  const wallItems = [...priorPages, ...(res?.products ?? [])];
  const total = res?.total ?? wallItems.length;
  const hasMore = wallItems.length < total;
  const loadMore = () => {
    if (res?.products) setPriorPages((prev) => [...prev, ...(res.products ?? [])]);
    setPage((p) => p + 1);
  };
  return (
    <>
      <CardGrid>{wallItems.map((hit) => tile(hit, 'wall', true))}</CardGrid>
      <div className="flex items-center justify-center gap-3 pb-2">
        <span className={text.muted}>
          {countTpl.replace('{shown}', String(wallItems.length)).replace('{total}', String(total))}
        </span>
        {hasMore && (
          <button
            onClick={loadMore}
            disabled={isLoading}
            className={cn(
              text.body,
              'rounded-lg border border-border bg-card px-3 py-1.5 hover:border-primary/40 hover:bg-accent/30 transition-colors disabled:opacity-50',
            )}
          >
            {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : loadMoreLabel}
          </button>
        )}
      </div>
    </>
  );
}

/** The canvas header — query title, refine box, applied chips, and a
 *  pill-chip row per facet. The pill styling is a direct visual borrow
 *  of Discovery's ChipRow (swimlane-card.tsx) — see module docstring. */
function HybridHeader({
  query,
  total,
  refineText,
  onRefineChange,
  onRefineSubmit,
  refinePlaceholder,
  refineButtonLabel,
  feedback,
  facets,
  selectedOf,
  onToggle,
  chipsActive,
  onClearAll,
  clearAllLabel,
  priceFacet,
  priceFilter,
  onSetPriceRange,
  priceAnyLabel,
  hideFacetsLabel,
  showFacetsLabel,
  appliedLabel,
}: {
  query: string;
  total: number;
  refineText: string;
  onRefineChange: (v: string) => void;
  onRefineSubmit: () => void;
  refinePlaceholder: string;
  refineButtonLabel: string;
  feedback: string | null;
  facets: Facet[];
  selectedOf: (f: Facet) => string[];
  onToggle: (key: string, value: string) => void;
  chipsActive: boolean;
  onClearAll: () => void;
  clearAllLabel: string;
  priceFacet?: Facet;
  priceFilter?: { min?: number; max?: number };
  onSetPriceRange: (range: { min?: number; max?: number } | null) => void;
  priceAnyLabel: string;
  hideFacetsLabel: string;
  showFacetsLabel: string;
  appliedLabel: string;
}) {
  const nonRangeFacets = facets.filter((f) => f.type !== 'range');
  // Collapsed by default — the refine/facets panel starts closed so the
  // results grid gets the room on open; the user expands it on demand via
  // the chrome toggle below.
  const [collapsed, setCollapsed] = useState(true);

  return (
    <div className="sticky top-0 z-10 space-y-2 border-b border-border bg-background/95 px-4 py-3 backdrop-blur">
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-3 min-w-0">
          <span className="text-lg font-medium truncate">{query || 'All products'}</span>
          <span className={cn(text.muted, 'font-mono text-xs flex-shrink-0')}>{total.toLocaleString()} results</span>
        </div>
        {/* Icon-only chrome toggle — same pattern as Discovery's swimlane-card.tsx
            (SlidersHorizontal when collapsed, ChevronUp when open), not a text link. */}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="ml-2 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
          title={collapsed ? showFacetsLabel : hideFacetsLabel}
        >
          {collapsed ? (
            <SlidersHorizontal className="w-3.5 h-3.5" />
          ) : (
            <ChevronUp className="w-3.5 h-3.5" />
          )}
        </button>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5">
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary/70" />
          <input
            value={refineText}
            onChange={(e) => onRefineChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onRefineSubmit();
            }}
            placeholder={refinePlaceholder}
            className="min-w-0 flex-1 bg-transparent text-sm outline-none"
          />
        </div>
        <button
          onClick={onRefineSubmit}
          className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
        >
          {refineButtonLabel}
        </button>
      </div>

      {feedback && <div className={cn(text.muted, 'text-[11px]')}>{feedback}</div>}

      {/* Always-visible applied-chip summary — stays up even when the
          full facet picker is collapsed, so the current filter state
          doesn't disappear with it (the gap the "search as a
          conversation" sketch's terse header pointed at). */}
      {chipsActive && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={cn(text.muted, 'text-[11px] font-medium shrink-0')}>{appliedLabel}</span>
          {nonRangeFacets.flatMap((f) =>
            selectedOf(f).map((v) => {
              const fv = f.values?.find((x) => x.value === v);
              return (
                <button
                  key={`${f.key}:${v}`}
                  onClick={() => onToggle(f.key, v)}
                  className="flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] hover:bg-primary/20 transition-colors"
                >
                  {fv?.label ?? v}
                  <X className="w-2.5 h-2.5" />
                </button>
              );
            }),
          )}
          {priceFacet && priceFilter && (priceFilter.min != null || priceFilter.max != null) && (
            <button
              onClick={() => onSetPriceRange(null)}
              className="flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] hover:bg-primary/20 transition-colors"
            >
              {priceFacet.label}
              {priceFilter.max != null ? ` ≤ $${priceFilter.max.toLocaleString()}` : ''}
              {priceFilter.min != null ? ` ≥ $${priceFilter.min.toLocaleString()}` : ''}
              <X className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
      )}

      {!collapsed && (
        <div className="space-y-1">
          {nonRangeFacets.map((f) => (
            <FacetChipRow
              key={f.key}
              facet={f}
              selected={selectedOf(f)}
              onToggle={(v) => onToggle(f.key, v)}
            />
          ))}
          {priceFacet && (
            <PriceChipRow facet={priceFacet} value={priceFilter} onChange={onSetPriceRange} anyLabel={priceAnyLabel} />
          )}
        </div>
      )}

      {chipsActive && (
        <button
          onClick={onClearAll}
          className={cn('flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground')}
        >
          <X className="w-3 h-3" />
          {clearAllLabel}
        </button>
      )}
    </div>
  );
}

const PEEK = 8;

/** One facet as a horizontal pill row with counts — adapted from
 *  Discovery's ChipRow for multi-select (OR within a facet, vs.
 *  intent_grid's single-select pool facets). Dead values (0 count,
 *  not selected) dim instead of disappearing. */
function FacetChipRow({
  facet,
  selected,
  onToggle,
}: {
  facet: Facet;
  selected: string[];
  onToggle: (value: string) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const values = facet.values ?? [];
  if (values.length === 0) return null;
  const shown = showAll ? values : values.slice(0, PEEK);
  const hidden = values.length - shown.length;
  // Colour is a swatch, not a numbered bucket: it's matched by
  // perceptual distance (ΔE against the bucket's own measured
  // centroid), not exact equality, so a count here would promise a
  // precision the filter doesn't have — see COLOR_FILTER_DELTAE_MAX.
  const isColor = facet.key === 'primary_color';

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-[9px] uppercase tracking-wider text-muted-foreground w-16 shrink-0 flex items-center gap-1">
        {facet.label}
      </span>
      {shown.map((v) => {
        const on = selected.includes(v.value);
        const dead = v.count === 0 && !on;
        return (
          <button
            key={v.value}
            type="button"
            onClick={() => onToggle(v.value)}
            style={{ opacity: dead ? 0.4 : 1 }}
            className={cn(
              'flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full transition-colors',
              on
                ? 'bg-primary text-primary-foreground'
                : 'bg-background border border-border hover:bg-accent',
            )}
            title={isColor ? (v.label ?? v.value) : `${v.count} in pool`}
          >
            {isColor && v.swatch && (
              <span
                className="h-2.5 w-2.5 rounded-full border border-black/10 shrink-0"
                style={{ backgroundColor: v.swatch }}
              />
            )}
            {v.label ?? v.value}
            {!isColor && <span className="ml-1 opacity-60">{v.count}</span>}
          </button>
        );
      })}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="text-[10px] text-muted-foreground hover:text-foreground"
        >
          +{hidden} more
        </button>
      )}
    </div>
  );
}

/** Price — same pill-row shell as FacetChipRow, but the body is a
 *  single max-price slider instead of value pills (the backend's
 *  range facet has no discrete values to show as chips). */
function PriceChipRow({
  facet,
  value,
  onChange,
  anyLabel,
}: {
  facet: Facet;
  value?: { min?: number; max?: number };
  onChange: (range: { min?: number; max?: number } | null) => void;
  anyLabel: string;
}) {
  const lo = facet.min ?? 0;
  const hi = facet.max ?? 0;
  const current = value?.max ?? hi;
  const isAny = value?.max == null;

  return (
    <div className="flex items-center gap-2">
      <span className="text-[9px] uppercase tracking-wider text-muted-foreground w-16 shrink-0">
        {facet.label}
      </span>
      <input
        type="range"
        min={lo}
        max={hi || 1}
        step={Math.max(1, Math.round((hi - lo) / 100) || 1)}
        value={current}
        onChange={(e) => {
          const v = Number(e.target.value);
          onChange(v >= hi ? null : { ...value, max: v });
        }}
        className="h-3 w-40"
      />
      <span className="text-[10px] text-muted-foreground tabular-nums">
        {isAny ? anyLabel : `$${current.toLocaleString()}`}
      </span>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-3 text-center">
      {children}
    </div>
  );
}
